import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { NgClass } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { EventModel } from '@app/events/shared/event.model';
import {
  EventGuestChoicesModel,
  EventGuestDietaryAttachmentModel,
  EventGuestImportPreview,
  EventGuestImportPreviewRow,
  EventGuestImportResult,
  EventGuestModel,
  EventGuestPermissionsModel,
  EventGuestSummaryModel,
} from '@app/events/shared/event-guest.model';
import { EventGuestService } from '@app/events/shared/event-guest.service';
import { FeatureToggleService } from '@app/feature-toggles/feature-toggle.service';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { ModalComponent } from '@app/partials/modal/modal.component';
import { TextInputComponent } from '@app/partials/form/text-input/text-input.component';
import { TextareaComponent } from '@app/partials/form/textarea/textarea.component';
import { SelectComponent } from '@app/partials/form/select/select.component';
import { DataTableComponent } from '@app/partials/datatable/data-table/data-table.component';
import { Column } from '@app/partials/datatable/data-table/column';
import { RowAction } from '@app/partials/datatable/data-table/row-action';
import { ToastService } from '@app/shared/toast/toast.service';
import { ConfirmationModalService } from '@app/partials/modal/confirmation-modal.service';
import { PagedEntities } from '@app/shared/abstract/paged-entities';
import { AbstractEntityEditComponent } from '@app/shared/abstract/abstract-entity-edit.component';
import { SignedUploadService } from '@app/shared/uploads/signed-upload.service';
import { Download, FileUp, LucideAngularModule, PenSquare, Plus, Search, Upload, UtensilsCrossed, Trash2 } from 'lucide-angular';
import { GuestNameColumnComponent } from '@app/events/view/guest-name-column.component';
import { GuestAllergensColumnComponent } from '@app/events/view/guest-allergens-column.component';
import { GuestFlagsColumnComponent } from '@app/events/view/guest-flags-column.component';
import { GuestAccommodationColumnComponent } from '@app/events/view/guest-accommodation-column.component';
import { GuestInlineSelectColumnComponent } from '@app/events/view/guest-inline-select-column.component';

type GuestsStage = 'upload' | 'list';

@Component({
  selector: 'app-event-guests',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgClass,
    LucideAngularModule,
    ButtonComponent,
    ModalComponent,
    TextInputComponent,
    TextareaComponent,
    SelectComponent,
    DataTableComponent,
  ],
  templateUrl: './guests.component.html',
  styleUrls: [],
})
export class EventGuestsComponent implements OnInit, OnChanges {
  @Input({ required: true }) public event!: EventModel;
  @Input() public mobileMinimal = false;
  // TVG-142: when set (from a Table Plan guest-tile click), open this guest's
  // edit modal once the list has loaded, then emit guestModalOpened to clear it.
  @Input() public openGuestId: number | null = null;
  @Output() public guestModalOpened = new EventEmitter<void>();

  // TVG-142: the high-chair flag belongs to the Table Plan canvas feature, so
  // its checkbox on the guest record only shows when that toggle is enabled
  // (default off on prod). Keeps the whole feature behind one switch.
  protected get highChairEnabled(): boolean {
    return this.featureToggleService.isEnabled('table-plan-canvas');
  }

  protected readonly UploadIcon = Upload;
  protected readonly FileUpIcon = FileUp;
  protected readonly DownloadIcon = Download;
  protected readonly PlusIcon = Plus;
  protected readonly SearchIcon = Search;
  protected readonly EditIcon = PenSquare;
  protected readonly DietaryIcon = UtensilsCrossed;
  protected readonly TrashIcon = Trash2;

  protected stage: GuestsStage = 'upload';
  protected loading = false;
  protected importPreviewLoading = false;
  protected importConfirming = false;
  protected loadError: string | null = null;
  protected guests: EventGuestModel[] = [];
  protected summary: EventGuestSummaryModel = {
    total_guests: 0,
    total_day_guests: 0,
    total_evening_guests: 0,
    adults: 0,
    teens: 0,
    children: 0,
    meat_eaters: 0,
    vegetarians_and_vegans: 0,
    with_requirements: 0,
    with_allergens: 0,
    airborne_allergies: 0,
    forms_uploaded: 0,
  };
  protected permissions: EventGuestPermissionsModel = {
    can_import: false,
    can_manage_guests: false,
    can_manage_dietary: false,
  };
  protected choices: EventGuestChoicesModel = {
    attendance: [],
    guest_type: [],
    menu_preference: [],
    allergens: [],
  };
  protected importPreview: EventGuestImportPreview | null = null;
  protected showImportPreview = false;
  // TVG-270: row numbers of possible-duplicate rows the user has chosen to add
  // anyway, and the on-screen summary shown once an import has run.
  protected confirmedDuplicateRows = new Set<number>();
  protected importResult: EventGuestImportResult | null = null;
  protected selectedGuestIds = new Set<number>();
  protected expandedGuestId: number | null = null;
  protected showGuestModal = false;
  protected savingGuest = false;
  protected editingGuest: EventGuestModel | null = null;
  protected showDietaryModal = false;
  protected savingDietary = false;
  protected dietaryGuest: EventGuestModel | null = null;
  protected dietarySaveSuccess = false;
  protected dietaryFormError: string | null = null;
  protected pendingDietaryFiles: File[] = [];
  protected removedDietaryAttachmentIds = new Set<number>();

  protected searchControl = new FormControl('');
  protected showDietaryOnlyControl = new FormControl(false, { nonNullable: true });
  protected attendanceFilterControl = new FormControl<string | null>(null);

  protected guestForm = new FormGroup({
    first_name: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(255)] }),
    last_name: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(255)] }),
    email: new FormControl('', { nonNullable: true, validators: [Validators.email, Validators.maxLength(255)] }),
    mobile: new FormControl('', { nonNullable: true, validators: [Validators.maxLength(255)] }),
    // TVG-269: age group and type of guest have no default — they start unset
    // ("Please Select") and are mandatory, so a guest cannot be saved until the
    // user picks each one.
    guest_type: new FormControl<string | null>(null, { validators: [Validators.required] }),
    attendance: new FormControl<string | null>(null, { validators: [Validators.required] }),
    menu_preference: new FormControl<string | null>(null),
    requires_accommodation: new FormControl(false, { nonNullable: true }),
    requires_high_chair: new FormControl(false, { nonNullable: true }),
  });

  protected dietaryForm = new FormGroup({
    event_guest_id: new FormControl<number | null>(null, [Validators.required]),
    menu_preference: new FormControl<string | null>(null),
    allergens: new FormControl<string[]>([], { nonNullable: true }),
    airborne_allergy: new FormControl<boolean | null>(false),
    religious_or_lifestyle: new FormControl<boolean | null>(false),
    religious_details: new FormControl('', { nonNullable: true, validators: [Validators.maxLength(5000)] }),
    additional_notes: new FormControl('', { nonNullable: true, validators: [Validators.maxLength(5000)] }),
  });

  private importFile: File | null = null;

  // TVG-41: after an inline row edit, quietly refetch so the summary chips and
  // day/evening counts stay accurate. Bound once so the column input reference
  // is stable across change detection.
  protected readonly handleInlineSaved = (): void => {
    void this.refreshGuestsQuietly();
  };

  // TVG-268: the inline age-group / type-of-guest dropdowns on the admin guest
  // list showed no options because this column set used to be a getter that
  // allocated a brand-new array (with new column + `inputs` objects) on every
  // change-detection cycle. The data table tracks columns by identity, so each
  // cycle destroyed and recreated the inline <app-select>s — closing any
  // dropdown the instant it was opened. Building the columns once, after the
  // choices and permissions load, keeps their identity stable so the dropdowns
  // stay open and render their options.
  protected guestTableColumns: Column<EventGuestModel>[] = [];

  private buildGuestTableColumns(): Column<EventGuestModel>[] {
    return [
      {
        field: 'full_name',
        header: 'Guest',
        sortable: true,
        width: '24%',
        component: GuestNameColumnComponent,
        mobileInputs: { nameOnly: this.mobileMinimal },
      },
      {
        // TVG-41: age group is inline-editable from the row, reusing the same
        // options/permissions as the full guest record.
        field: 'guest_type',
        header: 'Age group',
        width: '14%',
        component: GuestInlineSelectColumnComponent,
        inputs: {
          field: 'guest_type',
          choices: this.choices.guest_type,
          canEdit: this.permissions.can_manage_guests,
          onSaved: this.handleInlineSaved,
        },
        mobileHidden: this.mobileMinimal,
      },
      {
        // TVG-41: type of guest (attendance) is inline-editable from the row.
        field: 'attendance',
        header: 'Type of guest',
        width: '16%',
        component: GuestInlineSelectColumnComponent,
        inputs: {
          field: 'attendance',
          choices: this.choices.attendance,
          canEdit: this.permissions.can_manage_guests,
          onSaved: this.handleInlineSaved,
        },
      },
      {
        // TVG-41: menu choice is inline-editable from the row, following the
        // dietary permission rules used by the full record.
        field: 'dietary',
        header: 'Menu',
        width: '14%',
        component: GuestInlineSelectColumnComponent,
        inputs: {
          field: 'menu',
          choices: this.choices.menu_preference,
          canEdit: this.permissions.can_manage_dietary,
          onSaved: this.handleInlineSaved,
        },
        mobileHidden: this.mobileMinimal,
      },
      {
        field: 'dietary',
        header: 'Allergens',
        width: '18%',
        component: GuestAllergensColumnComponent,
        mobileHidden: this.mobileMinimal,
      },
      {
        field: 'dietary',
        header: 'Flags',
        width: '14%',
        component: GuestFlagsColumnComponent,
        mobileHidden: this.mobileMinimal,
      },
      {
        // TVG-267: read-only green-tick / empty indicator. Previously this
        // column rendered the raw boolean ("true") for viewers and an
        // interactive toggle for managers; both are replaced by a single
        // readable indicator. The flag is set from the Add/Edit guest modal
        // and the bulk "Mark as accommodation" action.
        field: 'requires_accommodation',
        header: 'Accommodation',
        width: '12%',
        align: 'center',
        component: GuestAccommodationColumnComponent,
      },
    ];
  }

  protected readonly guestRowActions: RowAction<EventGuestModel>[] = [
    {
      icon: this.EditIcon,
      callback: (guest) => this.openGuestModal(guest),
      condition: () => this.permissions.can_manage_guests,
      classes: 'text-gray-500 hover:text-brand-600 dark:text-gray-400 dark:hover:text-brand-400',
      title: () => 'Edit guest',
    },
    {
      icon: this.DietaryIcon,
      callback: (guest) => this.openDietaryModal(guest),
      condition: () => this.permissions.can_manage_dietary,
      classes: 'text-gray-500 hover:text-brand-600 dark:text-gray-400 dark:hover:text-brand-400',
      title: (guest) => guest.has_dietary ? 'Edit dietary' : 'Add dietary',
    },
    {
      icon: this.TrashIcon,
      callback: (guest) => this.removeGuest(guest),
      condition: () => this.permissions.can_manage_guests,
      classes: 'text-gray-500 hover:text-error-500 dark:text-gray-400 dark:hover:text-error-500',
      title: () => 'Remove guest',
    },
  ];

  constructor(
    private guestService: EventGuestService,
    private signedUploadService: SignedUploadService,
    private toastService: ToastService,
    private confirmationModal: ConfirmationModalService,
    private featureToggleService: FeatureToggleService,
  ) {
    this.dietaryForm.valueChanges.subscribe(() => {
      this.dietaryFormError = null;
      this.clearDietaryServerErrors();
    });

    this.dietaryForm.controls.event_guest_id.valueChanges.subscribe((guestId) => {
      if (!this.showDietaryModal || !guestId || guestId === this.dietaryGuest?.id) {
        return;
      }

      const selectedGuest = this.guests.find((guest) => guest.id === guestId);

      if (!selectedGuest) {
        return;
      }

      this.populateDietaryForm(selectedGuest);
    });
  }

  ngOnInit(): void {
    this.loadGuests();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['event'] && !changes['event'].firstChange && changes['event'].currentValue?.id !== changes['event'].previousValue?.id) {
      this.loadGuests();
    }

    // TVG-142: a guest-tile click on the Table Plan canvas pushes the id here.
    // Open it now if the list is already loaded; otherwise loadGuests() opens it.
    if (changes['openGuestId'] && this.openGuestId != null && this.guests.length > 0) {
      this.maybeOpenRequestedGuest();
    }

    // TVG-268: the memoised columns depend on mobileMinimal, so rebuild them
    // when it changes after the first render (the initial build happens in
    // loadGuests once choices and permissions are known).
    if (changes['mobileMinimal'] && !changes['mobileMinimal'].firstChange) {
      this.guestTableColumns = this.buildGuestTableColumns();
    }
  }

  protected filteredGuests(): EventGuestModel[] {
    const search = (this.searchControl.value ?? '').trim().toLowerCase();
    const dietaryOnly = this.showDietaryOnlyControl.value;
    const attendance = this.attendanceFilterControl.value;

    return this.guests.filter((guest) => {
      if (search && !(`${guest.full_name} ${guest.email ?? ''} ${guest.mobile ?? ''}`.toLowerCase().includes(search))) {
        return false;
      }

      if (dietaryOnly && !guest.dietary?.has_requirements) {
        return false;
      }

      if (attendance && guest.attendance !== attendance) {
        return false;
      }

      return true;
    });
  }

  protected guestTablePage(): PagedEntities<EventGuestModel> {
    const guests = this.filteredGuests();

    return {
      data: guests,
      meta: {
        current_page: 1,
        from: guests.length > 0 ? 1 : 0,
        last_page: 1,
        path: '',
        per_page: guests.length || 1,
        to: guests.length,
        total: guests.length,
      },
    };
  }

  protected guestSelectChoices(): { value: number; label: string }[] {
    return this.guests.map((guest) => ({
      value: guest.id,
      label: guest.full_name,
    }));
  }

  protected attendanceChoices(): { value: string; label: string }[] {
    return this.choices.attendance.map((value) => ({ value, label: this.attendanceLabel(value) }));
  }

  protected guestTypeChoices(): { value: string; label: string }[] {
    return this.choices.guest_type.map((value) => ({ value, label: this.guestTypeLabel(value) }));
  }

  protected menuPreferenceChoices(): { value: string; label: string }[] {
    return this.choices.menu_preference.map((value) => ({ value, label: value }));
  }

  protected statusBadgeClass(status: EventGuestImportPreviewRow['status']): string {
    return {
      new: 'bg-emerald-50 text-emerald-700',
      duplicate: 'bg-amber-50 text-amber-700',
      invalid: 'bg-red-50 text-red-700',
    }[status];
  }

  protected statusLabel(status: EventGuestImportPreviewRow['status']): string {
    return {
      new: 'New',
      duplicate: 'Possible duplicate',
      invalid: 'Invalid',
    }[status];
  }

  // TVG-270: a possible-duplicate row is only imported when the user ticks it.
  protected isDuplicateConfirmed(row: number): boolean {
    return this.confirmedDuplicateRows.has(row);
  }

  protected toggleDuplicateConfirm(row: number, checked: boolean): void {
    if (checked) {
      this.confirmedDuplicateRows.add(row);
      return;
    }

    this.confirmedDuplicateRows.delete(row);
  }

  // TVG-270: how many rows the confirm button will import — every new row plus
  // the possible duplicates the user has chosen to add anyway.
  protected importableCount(): number {
    if (!this.importPreview) {
      return 0;
    }

    return this.importPreview.summary.new_count + this.confirmedDuplicateRows.size;
  }

  protected triggerFilePicker(input: HTMLInputElement): void {
    input.click();
  }

  protected dietaryAttachments(): EventGuestDietaryAttachmentModel[] {
    const attachments = this.dietaryGuest?.dietary?.attachments ?? [];

    return attachments.filter((attachment) => !this.removedDietaryAttachmentIds.has(attachment.id));
  }

  protected isAttachmentMarkedForRemoval(attachmentId: number): boolean {
    return this.removedDietaryAttachmentIds.has(attachmentId);
  }

  protected toggleAttachmentRemoval(attachmentId: number): void {
    if (this.removedDietaryAttachmentIds.has(attachmentId)) {
      this.removedDietaryAttachmentIds.delete(attachmentId);
      this.dietarySaveSuccess = false;
      this.dietaryFormError = null;
      return;
    }

    this.removedDietaryAttachmentIds.add(attachmentId);
    this.dietarySaveSuccess = false;
    this.dietaryFormError = null;
  }

  protected async downloadTemplate(): Promise<void> {
    if (!this.event?.id) {
      return;
    }

    try {
      const blob = await firstValueFrom(this.guestService.downloadTemplate(this.event.id));
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'tvg-guests-template.csv';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading guest template:', error);
      this.toastService.error('Unable to download the guest template.');
    }
  }

  protected async onImportFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;

    if (!file || !this.event?.id) {
      input.value = '';
      return;
    }

    this.importFile = file;
    this.importPreviewLoading = true;
    this.confirmedDuplicateRows.clear();
    this.importResult = null;

    try {
      const formData = new FormData();
      formData.append('guest_file', file, file.name);
      this.importPreview = await firstValueFrom(this.guestService.previewImport(this.event.id, formData));
      this.showImportPreview = true;
    } catch (error: any) {
      console.error('Error previewing guest import:', error);
      this.toastService.error(this.errorMessage(error) || 'Unable to preview the guest file.');
    } finally {
      this.importPreviewLoading = false;
      input.value = '';
    }
  }

  protected async confirmImport(): Promise<void> {
    if (!this.event?.id || !this.importFile || !this.importPreview || this.importConfirming) {
      return;
    }

    this.importConfirming = true;

    try {
      const formData = new FormData();
      formData.append('guest_file', this.importFile, this.importFile.name);
      // TVG-270: tell the backend which possible-duplicate rows to add anyway.
      this.confirmedDuplicateRows.forEach((row) => formData.append('confirmed_duplicates[]', String(row)));

      this.importResult = await firstValueFrom(this.guestService.importGuests(this.event.id, formData));
      this.toastService.success(this.importResult.message);
      // Keep the modal open to show the on-screen summary; the refreshed list
      // is loaded underneath so it is up to date once the summary is closed.
      await this.loadGuests();
    } catch (error: any) {
      console.error('Error importing guest file:', error);
      this.toastService.error(this.errorMessage(error) || 'Unable to import the guest file.');
    } finally {
      this.importConfirming = false;
    }
  }

  protected cancelImportPreview(): void {
    if (this.importConfirming) {
      return;
    }

    this.showImportPreview = false;
    this.importPreview = null;
    this.importFile = null;
    this.confirmedDuplicateRows.clear();
    this.importResult = null;
    this.stage = this.guests.length > 0 ? 'list' : 'upload';
  }

  /**
   * TVG-142: open the edit modal for the guest the Table Plan canvas asked for,
   * then signal the parent so it clears the request (avoiding a re-open).
   */
  private maybeOpenRequestedGuest(): void {
    if (this.openGuestId == null) {
      return;
    }
    const guest = this.guests.find((g) => g.id === this.openGuestId);
    if (!guest) {
      return;
    }
    this.openGuestModal(guest);
    this.guestModalOpened.emit();
  }

  protected openGuestModal(guest?: EventGuestModel): void {
    this.editingGuest = guest ?? null;
    this.guestForm.reset({
      first_name: guest?.first_name ?? '',
      last_name: guest?.last_name ?? '',
      email: guest?.email ?? '',
      mobile: guest?.mobile ?? '',
      guest_type: guest?.guest_type ?? null,
      attendance: guest?.attendance ?? null,
      menu_preference: guest?.dietary?.menu_preference ?? null,
      requires_accommodation: guest?.requires_accommodation ?? false,
      requires_high_chair: guest?.requires_high_chair ?? false,
    });
    this.showGuestModal = true;
  }

  protected closeGuestModal(): void {
    if (this.savingGuest) {
      return;
    }

    this.showGuestModal = false;
    this.editingGuest = null;
  }

  protected async saveGuest(): Promise<void> {
    if (!this.event?.id || this.savingGuest) {
      return;
    }

    this.guestForm.markAllAsTouched();

    if (this.guestForm.invalid) {
      return;
    }

    this.savingGuest = true;

    try {
      const payload = this.guestForm.getRawValue();

      if (this.editingGuest) {
        await firstValueFrom(this.guestService.updateGuest(this.event.id, this.editingGuest.id, payload));
      } else {
        await firstValueFrom(this.guestService.createGuest(this.event.id, payload));
      }

      this.toastService.success(`Guest ${this.editingGuest ? 'updated' : 'added'} successfully.`);
      this.showGuestModal = false;
      this.editingGuest = null;
      await this.loadGuests();
    } catch (error: any) {
      console.error('Error saving guest:', error);
      this.toastService.error(this.errorMessage(error) || 'Unable to save guest details.');
    } finally {
      this.savingGuest = false;
    }
  }

  protected openDietaryModal(guest: EventGuestModel): void {
    this.populateDietaryForm(guest);
    this.showDietaryModal = true;
  }

  protected closeDietaryModal(): void {
    if (this.savingDietary) {
      return;
    }

    this.showDietaryModal = false;
    this.dietaryGuest = null;
    this.dietarySaveSuccess = false;
    this.dietaryFormError = null;
    this.pendingDietaryFiles = [];
    this.removedDietaryAttachmentIds.clear();
  }

  protected onDietaryFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);

    if (files.length === 0) {
      input.value = '';
      return;
    }

    this.pendingDietaryFiles = [...this.pendingDietaryFiles, ...files];
    this.dietarySaveSuccess = false;
    this.dietaryFormError = null;
    input.value = '';
  }

  protected removePendingDietaryFile(index: number): void {
    this.pendingDietaryFiles = this.pendingDietaryFiles.filter((_, itemIndex) => itemIndex !== index);
    this.dietarySaveSuccess = false;
    this.dietaryFormError = null;
  }

  protected canSaveDietary(): boolean {
    const { event_guest_id } = this.dietaryForm.getRawValue();

    return !this.savingDietary
      && !!event_guest_id
      && this.dietaryForm.valid
      && this.hasPendingDietaryChanges();
  }

  protected toggleExpandedGuest(guest: EventGuestModel): void {
    this.expandedGuestId = this.expandedGuestId === guest.id ? null : guest.id;
  }

  protected toggleGuestSelection(guestId: number, checked: boolean): void {
    if (checked) {
      this.selectedGuestIds.add(guestId);
      return;
    }

    this.selectedGuestIds.delete(guestId);
  }

  protected clearSelection(): void {
    this.selectedGuestIds.clear();
  }

  protected hasSelectedGuests(): boolean {
    return this.selectedGuestIds.size > 0;
  }

  protected selectedGuestCount(): number {
    return this.selectedGuestIds.size;
  }

  protected async markSelectedAccommodation(): Promise<void> {
    if (!this.event?.id || this.selectedGuestIds.size === 0) {
      return;
    }

    try {
      await firstValueFrom(this.guestService.markAccommodation(this.event.id, Array.from(this.selectedGuestIds)));
      this.toastService.success('Selected guests marked as accommodation.');
      this.clearSelection();
      await this.loadGuests();
    } catch (error: any) {
      console.error('Error marking accommodation:', error);
      this.toastService.error(this.errorMessage(error) || 'Unable to update accommodation.');
    }
  }

  protected async removeGuest(guest: EventGuestModel): Promise<void> {
    if (!this.event?.id) {
      return;
    }

    const confirmed = await this.confirmationModal.confirm({
      title: 'Remove Guest',
      message: `Remove ${guest.full_name} from this event?`,
      confirmLabel: 'Remove Guest',
      confirmButtonStyle: 'danger',
    });

    if (!confirmed) {
      return;
    }

    try {
      await firstValueFrom(this.guestService.deleteGuest(this.event.id, guest.id));
      this.toastService.success('Guest removed successfully.');
      await this.loadGuests();
    } catch (error: any) {
      console.error('Error removing guest:', error);
      this.toastService.error(this.errorMessage(error) || 'Unable to remove guest.');
    }
  }

  protected multiSelected(control: FormControl<string[]>, value: string): boolean {
    return control.value.includes(value);
  }

  protected toggleMultiChoice(control: FormControl<string[]>, value: string, checked: boolean): void {
    const current = [...control.value];
    const next = checked
      ? Array.from(new Set([...current, value]))
      : current.filter((item) => item !== value);

    control.setValue(next);
    control.markAsDirty();
    control.markAsTouched();
  }

  protected async saveDietary(): Promise<void> {
    if (!this.event?.id || this.savingDietary) {
      return;
    }

    if (!this.hasPendingDietaryChanges()) {
      return;
    }

    this.dietaryForm.markAllAsTouched();

    if (this.dietaryForm.invalid) {
      return;
    }

    this.savingDietary = true;
    this.dietaryFormError = null;
    this.clearDietaryServerErrors();
    this.dietaryForm.disable({ emitEvent: false });
    const minimumSavingState = this.wait(350);

    try {
      const dietaryPayload = this.dietaryForm.getRawValue();
      const selectedGuestId = dietaryPayload.event_guest_id;

      if (!selectedGuestId) {
        this.dietaryForm.controls.event_guest_id.markAsTouched();
        return;
      }

      const uploadedFiles = await Promise.all(
        this.pendingDietaryFiles.map((file) => this.signedUploadService.upload(file)),
      );

      await firstValueFrom(
        this.guestService.updateDietary(this.event.id, selectedGuestId, {
          ...dietaryPayload,
          attachment_keys: uploadedFiles.map((file) => file.key),
          attachment_original_names: uploadedFiles.map((file) => file.originalName),
          removed_attachment_ids: Array.from(this.removedDietaryAttachmentIds),
        }),
      );

      await minimumSavingState;
      this.dietarySaveSuccess = true;
      this.toastService.success('Dietary details saved successfully.');
      await this.loadGuests();
      const refreshedGuest = this.guests.find((guest) => guest.id === selectedGuestId);

      if (refreshedGuest) {
        this.populateDietaryForm(refreshedGuest);
      }
    } catch (error: any) {
      await minimumSavingState;
      console.error('Error saving dietary details:', error);
      AbstractEntityEditComponent.bindErrorsToForm(error?.error, this.dietaryForm);
      this.dietaryFormError = this.errorMessage(error) || 'Unable to save dietary details.';
      this.toastService.error(this.errorMessage(error) || 'Unable to save dietary details.');
    } finally {
      this.savingDietary = false;
      this.dietaryForm.enable({ emitEvent: false });
    }
  }

  private async loadGuests(): Promise<void> {
    if (!this.event?.id) {
      return;
    }

    this.loading = true;
    this.loadError = null;

    try {
      const response = await firstValueFrom(this.guestService.getGuests(this.event.id));
      this.guests = response.data;
      this.summary = response.meta.summary;
      this.permissions = response.meta.permissions;
      this.choices = response.meta.choices;
      // TVG-268: rebuild the memoised column set now that choices and
      // permissions are known, so the inline dropdowns receive their options.
      this.guestTableColumns = this.buildGuestTableColumns();
      this.event = {
        ...this.event,
        day_guests: response.meta.summary.total_day_guests,
        evening_guests: response.meta.summary.total_evening_guests,
      };

      if (!this.importPreview) {
        this.stage = this.guests.length > 0 ? 'list' : 'upload';
      }

      if (this.expandedGuestId !== null && !this.guests.some((guest) => guest.id === this.expandedGuestId)) {
        this.expandedGuestId = null;
      }

      // TVG-142: honour a pending open-guest request once the list is loaded.
      this.maybeOpenRequestedGuest();
    } catch (error: any) {
      console.error('Error loading guests:', error);
      this.loadError = this.errorMessage(error) || 'Unable to load guests.';
    } finally {
      this.loading = false;
    }
  }

  // TVG-41: refetch guests without toggling the loading overlay, used after an
  // inline row edit to keep the summary and counts in sync with the change.
  private async refreshGuestsQuietly(): Promise<void> {
    if (!this.event?.id) {
      return;
    }

    try {
      const response = await firstValueFrom(this.guestService.getGuests(this.event.id));
      this.guests = response.data;
      this.summary = response.meta.summary;
      this.permissions = response.meta.permissions;
      this.choices = response.meta.choices;
      // TVG-268: keep the memoised columns in sync with the refreshed choices.
      this.guestTableColumns = this.buildGuestTableColumns();
      this.event = {
        ...this.event,
        day_guests: response.meta.summary.total_day_guests,
        evening_guests: response.meta.summary.total_evening_guests,
      };
    } catch (error: any) {
      console.error('Error refreshing guests:', error);
    }
  }

  private errorMessage(error: any): string | null {
    const errors = error?.error?.errors;

    if (errors && typeof errors === 'object') {
      const firstError = Object.values(errors).flat().find(Boolean);
      if (typeof firstError === 'string') {
        return firstError;
      }
    }

    return error?.error?.message ?? null;
  }

  private populateDietaryForm(guest: EventGuestModel): void {
    this.dietaryGuest = guest;
    this.dietarySaveSuccess = false;
    this.dietaryFormError = null;
    this.pendingDietaryFiles = [];
    this.removedDietaryAttachmentIds.clear();
    this.dietaryForm.reset({
      event_guest_id: guest.id,
      menu_preference: guest.dietary?.menu_preference ?? null,
      allergens: guest.dietary?.allergens ?? [],
      airborne_allergy: guest.dietary?.airborne_allergy ?? false,
      religious_or_lifestyle: guest.dietary?.religious_or_lifestyle ?? false,
      religious_details: guest.dietary?.religious_details ?? '',
      additional_notes: guest.dietary?.additional_notes ?? '',
    }, { emitEvent: false });
  }

  private hasPendingDietaryChanges(): boolean {
    return this.dietaryForm.dirty
      || this.pendingDietaryFiles.length > 0
      || this.removedDietaryAttachmentIds.size > 0;
  }

  private clearDietaryServerErrors(): void {
    Object.values(this.dietaryForm.controls).forEach((control) => {
      if (!control.errors) {
        return;
      }

      const nextErrors = { ...control.errors };

      Object.keys(nextErrors).forEach((key) => {
        if (typeof nextErrors[key] === 'string') {
          delete nextErrors[key];
        }
      });

      control.setErrors(Object.keys(nextErrors).length > 0 ? nextErrors : null);
    });
  }

  private wait(ms: number): Promise<void> {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  private attendanceLabel(value: string): string {
    return value === 'Day & Evening' ? 'Day and Evening' : value;
  }

  private guestTypeLabel(value: string): string {
    return {
      Adult: 'Adult (18+)',
      Teen: 'Teen (11-17)',
      Child: 'Child (2-10)',
      Baby: 'Baby (under 2)',
    }[value] ?? value;
  }
}
